import { type FormEvent, useState } from 'react';

interface ReplyBoxProps {
  readonly from: string;
  readonly to: string;
  readonly sending: boolean;
  readonly error: string | null;
  readonly onSend: (text: string) => Promise<boolean>;
}

export function ReplyBox({ from, to, sending, error, onSend }: ReplyBoxProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  if (!open) {
    return (
      <div className="reply">
        <button className="btn" type="button" onClick={() => setOpen(true)}>
          Reply
        </button>
      </div>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!text.trim() || sending) return;

    // Closed and cleared only when the send actually succeeded — a failed
    // send that discarded the draft would be the worst possible outcome here.
    if (await onSend(text)) {
      setText('');
      setOpen(false);
    }
  };

  return (
    <form className="reply" onSubmit={submit}>
      <div className="reply__head">
        <span>From</span>
        <span className="reply__to">{from}</span>
        <span>to</span>
        <span className="reply__to">{to}</span>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error}
        </p>
      ) : null}

      <textarea
        className="reply__text"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Write a reply…"
        aria-label="Reply message"
        autoFocus
        disabled={sending}
      />

      <div className="reply__actions">
        <button className="btn btn--primary" type="submit" disabled={sending || !text.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </button>

        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => setOpen(false)}
          disabled={sending}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
